import { faFileCsv } from '@fortawesome/pro-light-svg-icons';
import type { Order } from '@partful/shared-types';
import useFormattedPrice from '@services/formatCurrency';
import { formatDateWithTime } from '@services/formatDate';
import { exportTableToCsv } from '@utils/csv';
import { ExportButtonBase, ExportButtonProps } from './ExportButton';

export function ExportCsvButton({
  orders,
  label,
  ...buttonProps
}: Omit<ExportButtonProps, 'type'> & { orders: Order[] }) {
  const ordersExportData = useOrdersExportData(orders);
  const handleExport = /* istanbul ignore next */ () => exportTableToCsv(ordersExportData, 'Orders');

  return (
    <ExportButtonBase
      {...buttonProps}
      type="csv"
      icon={faFileCsv}
      onClick={handleExport}
      label={label ?? /* istanbul ignore next */ 'Export table to CSV'}
    />
  );
}

/**
 * @returns {string[][]} - array of strings that represent order data.
 * The first array is like header (general order data), the rest are order items.
 */
function useOrderExportData(order: Order): string[][] {
  const id = order.eCommerceOrderName || order.orderId;
  const date = formatDateWithTime(order.timestamp);
  const { companyName } = order;
  const { amount: price, currencyCode: currency } = order.totalPrice;
  const formattedPrice = useFormattedPrice({ price, currency });
  const total = typeof formattedPrice === 'string' ? formattedPrice : /* istanbul ignore next */ 'Price on request';
  const { status } = order;
  const { items } = order;
  const orderArray = [id, date, companyName, total, status, '', '', '', ''];
  const orderItemsArrays = items.map((item) => {
    const { partNumber, partName, unitPrice, quantity } = item;
    return ['', '', '', '', '', partName, partNumber, String(quantity), String(unitPrice)];
  });
  return [orderArray, ...orderItemsArrays];
}

function useOrdersExportData(orders: Order[]) {
  const header = [
    'Confirmation number',
    'Date',
    'Company name',
    'Total',
    'Status',
    'Part Ordered',
    'Part Number',
    'Quantity',
    'Part Cost',
  ];
  const ordersArrays = orders.flatMap(useOrderExportData);
  return [header, ...ordersArrays];
}
